/**
 * Exportação de uma resposta de formulário para PDF.
 *
 * Sem dependências externas: monta um documento HTML estilizado num iframe
 * oculto e dispara o diálogo de impressão do navegador (Salvar como PDF).
 * Recebe o objeto retornado por FormsApi.getInsumo (endpoint .../insumo).
 */
import { RISK_COLOR, fmtDateTime as fmtDate } from "../formsUtils";
import { laudoDaTriagem } from "./triagemDigital";

function esc(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const ESCALA = ["Muito Baixo", "Baixo", "Intermediário", "Alto", "Muito Alto"];

const VAD = [
  ["MALLAMPATI", ["I", "II", "III", "IV"]],
  ["PROTUSÃO DA MANDÍBULA NORMAL?", ["SIM", "NÃO"]],
  ["ABERTURA DE BOCA < 3 CM", ["SIM", "NÃO"]],
  ["APNÉIA DO SONO?", ["SIM", "NÃO"]],
  ["DISTÂNCIA TIREO-MENTO > 6 CM?", ["SIM", "NÃO"]],
  ["MOVIMENTAÇÃO CERVICAL LIMITADA?", ["SIM", "NÃO"]],
];

function risco(valor) {
  const c = RISK_COLOR[valor];
  return `<span class="badge" style="${c ? `border-color:${c};color:${c}` : ""}">${esc(valor) || "—"}</span>`;
}

function lista(itens, vazio) {
  return itens.length ? itens.map((i) => `<div class="li">${i}</div>`).join("") : `<div class="li empty">${vazio}</div>`;
}

// Mesmo conteúdo do PDF "Triagem Eletrônica" do robô; VAD e Observações ficam em branco
// para o anestesista preencher, como lá.
function laudoHtml(laudo, patient) {
  const grupos = laudo.grupos.map((g) => `
    <div class="bloco"><h3>${esc(g.titulo)}</h3>
      ${lista(g.itens.map((c) => `${esc(c.nome)} ${risco(c.risco)}`), "Nenhuma")}
    </div>`).join("");
  const escala = laudo.estratificacao.map((e) => `
    <div class="estrato"><span class="estrato-nome">${esc(e.nome)}</span>
      <div class="escala">${ESCALA.map((n) => {
        const c = RISK_COLOR[n];
        return `<span style="${n === e.valor ? `background:${c};color:#fff;border-color:${c}` : ""}">${n}</span>`;
      }).join("")}</div>
    </div>`).join("");
  const vad = VAD.map(([rotulo, opcoes]) => `
    <div class="vad"><strong>${esc(rotulo)}</strong>
      <div>${opcoes.map((o) => `<span class="caixa"></span> ${esc(o)}`).join("&nbsp;&nbsp;&nbsp;")}</div>
    </div>`).join("");
  return `
    <div class="section">
      <div class="risco-topo" style="background:${RISK_COLOR[laudo.riscoPaciente] || "#777"}">Risco do Paciente: ${esc(laudo.riscoPaciente)}</div>
      <div class="asa">Classificação: <strong>${esc(laudo.asa)}</strong></div>
    </div>
    <div class="section">
      <h2 class="title">Dados gerais</h2>
      <div class="patient">
        <div><strong>Nome completo</strong>${esc(patient.name) || "-"}</div>
        <div><strong>CPF</strong>${esc(patient.external_ref) || "-"}</div>
        <div><strong>Sexo ao nascimento</strong>${esc(laudo.sexo) || "-"}</div>
        <div><strong>Altura/Peso</strong>${esc(laudo.alturaPeso)}</div>
        <div><strong>Idade</strong>${esc(laudo.idade)}</div>
        <div><strong>Telefone</strong>${esc(laudo.telefone) || "-"}</div>
        <div><strong>Cirurgião</strong>${esc(laudo.cirurgiao) || "-"}</div>
      </div>
    </div>
    <div class="section">
      <h2 class="title">Dados clínicos</h2>
      <div class="grade">
        <div class="bloco"><h3>Condições gerais</h3>
          <div class="li">Gravidez: ${esc(laudo.gravidez)}</div>
          <div class="li">IMC: ${esc(laudo.imc)} ${risco(laudo.riscoImc)}</div>
        </div>
        <div class="bloco"><h3>Hábitos sociais</h3>
          <div class="li">Etilismo: ${esc(laudo.habitos.etilismo)}</div>
          <div class="li">Tabagismo: ${esc(laudo.habitos.tabagismo)} | Período: ${esc(laudo.habitos.periodo) || "-"}</div>
          <div class="li">Outras Drogas: ${esc(laudo.habitos.drogas)}</div>
        </div>
        ${grupos}
        <div class="bloco"><h3>Medicações em uso</h3>
          ${lista(laudo.medicamentos.map((m) => `${esc(m.nome)} — Suspensão: ${esc(m.suspensao)}`), "Nenhuma")}
        </div>
        <div class="bloco"><h3>Alergias anteriores</h3>${lista(laudo.alergias.map(esc), "Nenhuma")}</div>
        <div class="bloco"><h3>Histórico de cirurgias anteriores</h3>${lista(laudo.tiposAnestesia.map(esc), "Nenhum")}</div>
        <div class="bloco"><h3>Ansiedade/desejo da informação</h3>
          <div class="li">Sentimentos sobre a anestesia: ${esc(laudo.sentimentos)}</div>
          <div class="li">Desejo de detalhes: ${esc(laudo.desejoDetalhes)}</div>
          <div class="li">Desejo de consulta: ${esc(laudo.desejoConsulta)}</div>
        </div>
      </div>
    </div>
    <div class="section"><h2 class="title">Previsão de VAD</h2><div class="grade">${vad}</div></div>
    <div class="section"><h2 class="title">Estratificação de risco</h2>${escala}</div>
    <div class="section"><h2 class="title">Observações</h2><div class="observacoes"></div></div>`;
}

export function exportResponsePdf(insumo) {
  if (!insumo) return;

  const patient = insumo.patient || {};
  const items = insumo.items || [];
  const scores = insumo.scores || [];
  const total = insumo.total_count || 0;
  const answered = insumo.answered_count || items.length;
  const isComplete = insumo.completeness === "completo";
  const pctFilled = total > 0 ? Math.round((answered / total) * 100) : 100;

  const laudo = laudoDaTriagem(insumo.clinical);

  const statusColor = isComplete ? "#2e7d32" : "#ef6c00";
  const statusLabel = isComplete ? "COMPLETO" : "PARCIAL";

  const scoresHtml = scores.length
    ? `<div class="scores">${scores
        .map((s) => {
          const c = RISK_COLOR[s.value];
          return `<span class="badge" style="${c ? `background:${c};color:#fff;border-color:${c}` : ""}">
            <em>${esc(s.name)}</em> ${esc(s.value)}
          </span>`;
        })
        .join("")}</div>`
    : "";

  const itemsHtml = items.length
    ? items
        .map(
          (it, i) => `
        <div class="qa ${i % 2 ? "alt" : ""}">
          <div class="q">${esc(it.label)}</div>
          <div class="a">${esc(it.value) || "<span class='empty'>—</span>"}</div>
        </div>`
        )
        .join("")
    : `<div class="empty-state">Nenhuma resposta registrada.</div>`;

  const html = `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<title>${esc(insumo.form_title || "Resposta")} — ${esc(patient.name || "")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #222; margin: 0;
         background: #f3f4f6; }
  .sheet { max-width: 820px; margin: 24px auto; background: #fff; border-radius: 10px; overflow: hidden;
           box-shadow: 0 2px 12px rgba(0,0,0,.08); }
  .header { background: linear-gradient(135deg, #1f3a93, #3f51b5); color: #fff; padding: 24px 32px; position: relative; }
  .header h1 { margin: 0; font-size: 20px; font-weight: 600; }
  .header .sub { opacity: .85; font-size: 12px; margin-top: 4px; letter-spacing: .5px; text-transform: uppercase; }
  .status { position: absolute; top: 24px; right: 32px; font-size: 12px; font-weight: 700; letter-spacing: 1px;
            padding: 6px 14px; border-radius: 20px; background: ${statusColor}; color: #fff; }
  .progress-wrap { padding: 0 32px; margin-top: -10px; }
  .progress { height: 8px; background: #e5e7eb; border-radius: 6px; overflow: hidden; margin-top: 16px; }
  .progress > span { display: block; height: 100%; width: ${pctFilled}%; background: ${statusColor}; }
  .progress-label { font-size: 11px; color: #6b7280; margin-top: 4px; }
  .section { padding: 20px 32px; }
  .patient { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; background: #f9fafb;
             border: 1px solid #eef0f3; border-radius: 8px; padding: 16px 20px; }
  .patient div { font-size: 13px; }
  .patient strong { color: #6b7280; font-weight: 600; display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; }
  h2.title { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #1f3a93;
             border-bottom: 2px solid #eef0f3; padding-bottom: 6px; margin: 0 0 12px; }
  .scores { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 4px; }
  .badge { border: 1px solid #cbd5e1; border-radius: 20px; padding: 5px 12px; font-size: 13px; font-weight: 600; }
  .badge em { font-style: normal; font-weight: 400; opacity: .8; margin-right: 4px; }
  .qa { display: grid; grid-template-columns: 38% 62%; gap: 0; padding: 9px 14px; font-size: 13px; border-radius: 6px; }
  .qa.alt { background: #f9fafb; }
  .qa .q { color: #555; font-weight: 600; padding-right: 12px; }
  .qa .a { color: #111; }
  .qa .a .empty { color: #bbb; }
  .empty-state { color: #999; font-size: 13px; padding: 12px; }
  .risco-topo { color: #fff; font-weight: 700; font-size: 16px; padding: 12px 16px; border-radius: 8px; text-align: center; }
  .asa { text-align: center; font-size: 13px; margin-top: 8px; }
  .grade { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; }
  .bloco h3 { font-size: 12px; color: #1f3a93; margin: 0 0 6px; }
  .li { font-size: 13px; padding: 3px 0; }
  .li.empty { color: #999; }
  .estrato { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; font-size: 12px; }
  .estrato-nome { width: 230px; font-weight: 600; color: #555; }
  .escala { display: flex; gap: 4px; }
  .escala span { border: 1px solid #e5e7eb; border-radius: 4px; padding: 3px 8px; color: #9ca3af; font-size: 11px; }
  .vad { font-size: 12px; }
  .vad strong { display: block; color: #555; margin-bottom: 4px; }
  .caixa { display: inline-block; width: 11px; height: 11px; border: 1px solid #555; border-radius: 50%; vertical-align: middle; }
  .observacoes { min-height: 120px; border: 1px solid #e5e7eb; border-radius: 8px; }
  .footer { padding: 16px 32px 28px; color: #9ca3af; font-size: 11px; border-top: 1px solid #eef0f3; margin-top: 8px;
            display: flex; justify-content: space-between; }
  @media print {
    body { background: #fff; }
    .sheet { box-shadow: none; margin: 0; max-width: 100%; border-radius: 0; }
    .qa, .badge, .patient, .bloco, .estrato, .vad { break-inside: avoid; }
    @page { margin: 12mm; }
  }
</style></head>
<body>
  <div class="sheet">
    <div class="header">
      <div class="status">${statusLabel}</div>
      <h1>${esc(insumo.form_title || "Resposta do formulário")}</h1>
      <div class="sub">Insumo para avaliação clínica</div>
    </div>
    <div class="progress-wrap">
      <div class="progress"><span></span></div>
      <div class="progress-label">${answered}${total ? ` de ${total}` : ""} campos respondidos${total ? ` (${pctFilled}%)` : ""}</div>
    </div>

    <div class="section">
      <div class="patient">
        <div><strong>Paciente</strong>${esc(patient.name) || "-"}</div>
        <div><strong>Telefone</strong>${esc(patient.phone) || "-"}</div>
        <div><strong>CPF / Documento</strong>${esc(patient.external_ref) || "-"}</div>
        <div><strong>Enviado em</strong>${fmtDate(insumo.submitted_at)}</div>
      </div>
    </div>

    ${laudo ? laudoHtml(laudo, patient) : ""}

    ${
      scores.length
        ? `<div class="section"><h2 class="title">Escore / Avaliação de risco</h2>${scoresHtml}</div>`
        : ""
    }

    <div class="section">
      <h2 class="title">Respostas</h2>
      ${itemsHtml}
    </div>

    <div class="footer">
      <span>Gerado em ${fmtDate(new Date())}</span>
      <span>TakaokaBots — Formulários</span>
    </div>
  </div>
  <script>
    window.onload = function () { window.focus(); window.print(); };
  </script>
</body></html>`;

  // Imprime por iframe oculto em vez de window.open: o bloqueador de pop-up é ligado por
  // padrão e derrubava a exportação num alerta. Iframe não passa pelo bloqueador, e a
  // saída é a mesma caixa de impressão do sistema (onde se escolhe "Salvar em PDF").
  const quadro = document.createElement("iframe");
  quadro.setAttribute("aria-hidden", "true");
  quadro.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(quadro);

  const doc = quadro.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  const remover = () => { if (quadro.parentNode) quadro.parentNode.removeChild(quadro); };
  // afterprint não dispara em todo navegador; o timer evita deixar o iframe pendurado.
  quadro.contentWindow.onafterprint = remover;
  setTimeout(remover, 60000);
}
